import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { bauvorhabenApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import type { Bauvorhaben, BauvorhabenStatus } from "@/types";
import {
  Search, Plus, HardHat, MapPin, CalendarRange, User, ListChecks,
  Loader2, AlertCircle, ChevronRight,
} from "lucide-react";

// Reine DATE-Spalten (YYYY-MM-DD) direkt als dd.MM.yyyy rendern —
// kein new Date(), damit keine Zeitzone ins Spiel kommt.
function formatDate(dateStr?: string | null): string {
  if (!dateStr) return "";
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dateStr;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

const statusLabels: Record<BauvorhabenStatus, string> = {
  geplant: "Geplant",
  aktiv: "Aktiv",
  abgeschlossen: "Abgeschlossen",
  abgebrochen: "Abgebrochen",
};

const statusBadgeClasses: Record<BauvorhabenStatus, string> = {
  geplant: "bg-gray-100 text-gray-800",
  aktiv: "bg-green-100 text-green-800",
  abgeschlossen: "bg-blue-100 text-blue-800",
  abgebrochen: "bg-red-100 text-red-800",
};

type StatusFilter = '' | BauvorhabenStatus;

const statusFilters: Array<{ value: StatusFilter; label: string }> = [
  { value: '', label: 'Alle' },
  { value: 'geplant', label: 'Geplant' },
  { value: 'aktiv', label: 'Aktiv' },
  { value: 'abgeschlossen', label: 'Abgeschlossen' },
  { value: 'abgebrochen', label: 'Abgebrochen' },
];

export function BauvorhabenPage() {
  const { isApprover } = useAuth();

  const [bauvorhaben, setBauvorhaben] = useState<Bauvorhaben[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError('');
    bauvorhabenApi
      .getAll({ page: 1, search: debouncedSearch, status: statusFilter })
      .then((response) => {
        if (cancelled) return;
        setBauvorhaben(response.data.data);
        const pagination = response.data.pagination;
        setHasMore(pagination ? pagination.page < pagination.total_pages : false);
      })
      .catch(() => {
        if (!cancelled) setError('Fehler beim Laden der Bauvorhaben');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, statusFilter]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    bauvorhabenApi
      .getAll({ page: nextPage, search: debouncedSearch, status: statusFilter })
      .then((response) => {
        setBauvorhaben((prev) => [...prev, ...response.data.data]);
        setHasMore((response.data.pagination?.page ?? nextPage) < (response.data.pagination?.total_pages ?? 0));
        setPage(nextPage);
      })
      .catch(() => setError('Fehler beim Laden der Bauvorhaben'))
      .finally(() => setIsLoadingMore(false));
  }, [isLoadingMore, hasMore, page, debouncedSearch, statusFilter]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { threshold: 1 }
    );
    if (observerTarget.current) observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Bauvorhaben</h1>
        {isApprover && (
          <div className="flex gap-2">
            <Link to="/bauvorhaben/gewerke" className="btn btn-secondary inline-flex items-center gap-2">
              <ListChecks className="h-4 w-4" />
              Gewerke verwalten
            </Link>
            <Link to="/bauvorhaben/new" className="btn btn-primary inline-flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Neues Bauvorhaben
            </Link>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Suchen — Name, Projektnummer, Ort…"
            className="input pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {statusFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                statusFilter === filter.value
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      ) : bauvorhaben.length === 0 && !error ? (
        <div className="card p-8 text-center text-gray-500">
          <HardHat className="mx-auto mb-3 h-8 w-8 text-gray-400" />
          <p>Keine Bauvorhaben gefunden</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {bauvorhaben.map((bvh) => (
            <Link
              key={bvh.id}
              to={`/bauvorhaben/${bvh.id}`}
              className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h2 className="font-semibold text-gray-900 leading-snug">{bvh.name}</h2>
                <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
              </div>
              {bvh.project_number && (
                <p className="text-sm text-gray-500 mb-2">Projekt-Nr. {bvh.project_number}</p>
              )}
              <span
                className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium mb-3 ${statusBadgeClasses[bvh.status]}`}
              >
                {statusLabels[bvh.status]}
              </span>
              <div className="space-y-1 text-sm text-gray-600">
                {(bvh.city || bvh.street) && (
                  <p className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
                    {[bvh.street, bvh.postal_code && `${bvh.postal_code} `, bvh.city].filter(Boolean).join(' ')}
                  </p>
                )}
                {bvh.bauleiter_name && (
                  <p className="flex items-center gap-1.5">
                    <User className="h-4 w-4 shrink-0 text-gray-400" />
                    Bauleiter: {bvh.bauleiter_name}
                  </p>
                )}
                {(bvh.start_date || bvh.end_date) && (
                  <p className="flex items-center gap-1.5">
                    <CalendarRange className="h-4 w-4 shrink-0 text-gray-400" />
                    {bvh.start_date && bvh.end_date
                      ? `${formatDate(bvh.start_date)} – ${formatDate(bvh.end_date)}`
                      : bvh.start_date
                      ? `ab ${formatDate(bvh.start_date)}`
                      : `bis ${formatDate(bvh.end_date)}`}
                  </p>
                )}
                <p className="flex items-center gap-1.5">
                  <HardHat className="h-4 w-4 shrink-0 text-gray-400" />
                  {bvh.bauherren_count ?? 0} Bauherren · {bvh.gewerke_fertig_count ?? 0} Gewerke fertig
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div ref={observerTarget} className="h-4" />
      {isLoadingMore && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
        </div>
      )}
    </div>
  );
}